import { useEffect, useState } from 'react';
import { Bike, CheckCircle2, TriangleAlert } from 'lucide-react';
import Drawer from './Drawer';
import Button from './Button';
import Avatar from './Avatar';
import { availableRiders as mockRiders } from '../data/mockData';
import styles from './AssignRiderDrawer.module.css';

export default function AssignRiderDrawer({ orders, onClose, onAssign, riders, activeRideCounts }) {
  const [selected, setSelected] = useState(null);
  const isOpen = !!orders?.length;
  const isBatch = (orders?.length ?? 0) > 1;
  const isReal = !!riders;
  const riderList = riders || mockRiders;
  const rideCounts = activeRideCounts || {};

  useEffect(() => {
    if (!isOpen) setSelected(null);
  }, [isOpen]);

  const areas = isBatch ? new Set(orders.map((o) => o.address?.split(',')[0] || 'unknown area')) : null;
  const mixedAreas = isBatch && areas.size > 1;

  return (
    <Drawer
      open={isOpen}
      onClose={onClose}
      title={isBatch ? `Assign a rider · ${orders.length} orders` : 'Assign a rider'}
      subtitle={
        isOpen
          ? isBatch
            ? orders.map((o) => o.id).join(' + ')
            : `Order ${orders[0].id}${orders[0].customer.name ? ` · ${orders[0].customer.name}` : ''}`
          : ''
      }
      width={400}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            icon={Bike}
            disabled={!selected}
            onClick={() => {
              onAssign(orders.map((o) => o.id), selected);
              onClose();
              setSelected(null);
            }}
          >
            {isBatch ? `Dispatch ${orders.length} orders` : 'Dispatch order'}
          </Button>
        </>
      }
    >
      {isBatch && (
        <>
          <p className={styles.hint}>These orders will be dispatched together, on one trip.</p>
          <div className={styles.stopsList}>
            {orders.map((o, i) => (
              <div className={styles.stopRow} key={o.id}>
                <span className={styles.stopIndex}>{i + 1}</span>
                <div className={styles.stopInfo}>
                  <p className={styles.stopName}>{o.customer.name || 'Customer'} <span className={styles.stopId}>· {o.id}</span></p>
                  {o.address && <p className={styles.stopAddress}>{o.address}</p>}
                </div>
              </div>
            ))}
          </div>
          {mixedAreas && (
            <p className={styles.notice}>
              <TriangleAlert size={13} /> These stops are in different areas — the trip may take longer than usual.
            </p>
          )}
        </>
      )}

      <p className={styles.hint}>
        Riders currently available near {isOpen ? (orders[0].address?.split(',')[0] || 'this order') : ''}.
      </p>
      <div className={styles.list}>
        {riderList.length === 0 && <p className={styles.hint}>No riders in the Delivery group yet.</p>}
        {riderList.map((rider) => (
          <button
            key={rider.id}
            className={`${styles.riderRow} ${selected === rider.id ? styles.selected : ''}`}
            disabled={!isReal && rider.status === 'busy'}
            onClick={() => setSelected(rider.id)}
          >
            <Avatar name={rider.name} size={38} />
            <div className={styles.riderInfo}>
              <p className={styles.riderName}>{rider.name || 'Rider'}</p>
              {rider.phone && rider.phone !== rider.name && (
                <p className={styles.riderPhone}>{rider.phone}</p>
              )}
              <div className={styles.statusRow}>
                {!isReal && (
                  <span className={`${styles.statusPill} ${rider.status === 'available' ? styles.available : styles.busy}`}>
                    {rider.status === 'available' ? 'Available' : 'On a run'}
                  </span>
                )}
                {!!rideCounts[rider.id] && (
                  <span className={styles.rideCount}>
                    {rideCounts[rider.id]} active order{rideCounts[rider.id] > 1 ? 's' : ''}
                  </span>
                )}
              </div>
            </div>
            {selected === rider.id && <CheckCircle2 size={18} className={styles.check} />}
          </button>
        ))}
      </div>
    </Drawer>
  );
}
